import React from 'react';
import { Helmet } from 'react-helmet';
import type { Selector } from 'react-redux';
import { useSelector } from 'react-redux';
import URL from 'url-parse';

import { FeatureFlag, RoutingKey } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';

const AlternateLinks: React.FC = () => {
  const { href, pathname } = useSelector(select.location.get);
  const locales = useSelector(select.site.getLocales);
  const strippedPathname = useSelector(select.location.stripLocale as Selector<RootState, string>);
  const alternateTags = useSelector(select.seo.get);
  const homepageOnlyAlternatesEnabled = useFeature(FeatureFlag.HomepageOnlyAlternates);
  const crossDomainAlternatesEnabled = useFeature(FeatureFlag.CrossDomainAlternates);
  const activeRoutingKey = useSelector(select.page.getActiveRoutingKey);
  const isHomepage = activeRoutingKey === `${RoutingKey.Home}`;

  if (!isHomepage && homepageOnlyAlternatesEnabled) {
    return null;
  }

  if (crossDomainAlternatesEnabled) {
    return (
      <Helmet>
        {alternateTags.map(({ url, hrefLang }) => (
          <link
            key={hrefLang}
            rel="alternate"
            hrefLang={hrefLang}
            href={new URL(pathname, url).href}
          />
        ))}
      </Helmet>
    );
  }

  return (
    <Helmet>
      {locales?.map((locale, index) => {
        const isDefaultLocale = index === 0;
        const url = new URL(href);

        if (!isDefaultLocale) {
          url.set('pathname', `/${locale}${strippedPathname}`);
        }

        return (
          <link
            key={locale}
            rel="alternate"
            hrefLang={isDefaultLocale ? 'x-default' : locale}
            href={`${url.origin}${url.pathname}`}
          />
        );
      })}
    </Helmet>
  );
};

export default AlternateLinks;
